import React, { lazy } from 'react';
import { PollConfig, PollFinals, PollQuestions, PollWelcomes } from 'schema';
import type { Question } from 'schema/poll';
import { replaceUnsafeClickMap } from '../containers/poll/ClickMapPoll/replaceUnsafeClickMap';
import { Final } from '../containers/poll/Final';
import { Welcome } from '../containers/poll/Welcome';
import { WelcomeMobileStories } from '../containers/poll/Welcome/WelcomeMobileStories';
import { QuestionWrapper } from '../containers/poll/Wrappers';
import { questionReplaceUnsafe, replaceUnsafeInput } from '../pixel/unsafe';
import Engine from './engine';

const AuthPoll = () => import('../containers/poll/AuthPoll').then(v => v.AuthPoll);
const ChoiceConjoint = () => import('../containers/poll/ChoiceConjoint').then(v => v.ChoiceConjoint);
const ClickMapPoll = () => import('../containers/poll/ClickMapPoll').then(v => v.ClickMapPoll);
const FilePoll = () => import('../containers/poll/FilePoll').then(v => v.FilePoll);
const MatrixMultiplePoll = () => import('../containers/poll/Matrix/MatrixMultiplePoll').then(v => v.MatrixMultiplePoll);
const MatrixRatingDigitsPoll = () =>
  import('../containers/poll/Matrix/MatrixRatingDigitsPoll').then(v => v.MatrixRatingDigitsPoll);
const MatrixRatingStarsPoll = () =>
  import('../containers/poll/Matrix/MatrixRatingStarsPoll').then(v => v.MatrixRatingStarsPoll);
const MatrixSinglePoll = () => import('../containers/poll/Matrix/MatrixSinglePoll').then(v => v.MatrixSinglePoll);
const MaxDiffPoll = () => import('../containers/poll/MaxDiffPoll').then(v => v.MaxDiffPoll);
const MediaFileAny = () => import('../containers/poll/MediaFileAny/MediaFileAny').then(v => v.MediaFileAny);
const MediaFileAudio = () => import('../containers/poll/MediaFileAudio').then(v => v.MediaFileAudio);
const MediaFileImage = () => import('../containers/poll/MediaFileImage').then(v => v.MediaFileImage);
const MediaFileText = () => import('../containers/poll/MediaFileText').then(v => v.MediaFileText);
const MediaFileVideo = () => import('../containers/poll/MediaFileVideo').then(v => v.MediaFileVideo);
const MultipleFromPicturePoll = () =>
  import('../containers/poll/MultipleFromPicturePoll/MultipleFromPicturePoll').then(v => v.MultipleFromPicturePoll);
const MultiplePoll = () => import('../containers/poll/MultiplePoll').then(v => v.MultiplePoll);
const MultipleSelectPoll = () => import('../containers/poll/MultipleSelectPoll').then(v => v.MultipleSelectPoll);
const RankPoll = () => import('../containers/poll/RankPoll').then(v => v.RankPoll);
const RatingDigitsPoll = () => import('../containers/poll/RatingDigitsPoll').then(v => v.RatingDigitsPoll);
const RatingLikePoll = () => import('../containers/poll/RatingLikePoll').then(v => v.RatingLikePoll);
const RatingSliderPoll = () => import('../containers/poll/RatingSliderPoll').then(v => v.RatingSliderPoll);
const RatingSmilesPoll = () => import('../containers/poll/RatingSmilesPoll').then(v => v.RatingSmilesPoll);
const RatingStarsPoll = () => import('../containers/poll/RatingStarsPoll').then(v => v.RatingStarsPoll);
const SelectPoll = () => import('../containers/poll/SelectPoll/SelectPoll').then(v => v.SelectPoll);
const SingleFromPicturePoll = () =>
  import('../containers/poll/SingleFromPicturePoll/SingleFromPicturePoll').then(v => v.SingleFromPicturePoll);
const SinglePoll = () => import('../containers/poll/SinglePoll').then(v => v.SinglePoll);
const TextareaPoll = () => import('../containers/poll/TextareaPoll/TextareaPoll').then(v => v.TextareaPoll);
const CardSortingCategoryPoll = () =>
  import('../containers/poll/CardSortingCategoryPoll/CardSortingCategoryPoll').then(v => v.CardSortingCategoryPoll);

const PrototypeScreen = () => import('../containers/poll/PrototypeScreen').then(v => v.PrototypeScreen);

class PollEngine extends Engine {
  getQuestionWrapper() {
    return QuestionWrapper;
  }

  override async questionReplaceUnsafeInput(
    question: { data: { type: string } },
    text: string | undefined,
  ): Promise<string | undefined> {
    if (text === undefined) return undefined;
    if (!questionReplaceUnsafe(question as Question)) return text;

    switch (question.data.type) {
      case 'POLL_FILE':
        return text;
      case 'POLL_CLICK_MAP':
        return replaceUnsafeClickMap(text, replaceUnsafeInput);
      default:
        return replaceUnsafeInput(text);
    }
  }

  override async preloadQuestion(type: string): Promise<void> {
    const questionConf = this.questionsMapping[type];
    if (questionConf) {
      const Component = await this.lastLoadPromise.then(() => questionConf.loader?.());
      if (!Component) return;
      questionConf.component = Component;
    }
  }

  override getQuestionComponent(type: string) {
    const mappings = this.questionsMapping;
    const { component } = mappings[type] as {
      config: PollConfig;
      component: React.FC;
    };
    return component;
  }

  private lastLoadPromise: Promise<unknown> = Promise.resolve();

  addQuestionAsync({ config }: { config: PollConfig }, loader: () => Promise<React.FC<any>>) {
    this.questionsMapping[config.name] = {
      config,
      loader,
      component: lazy(async () => {
        const loaderPromise = loader();
        this.lastLoadPromise = Promise.all([loaderPromise, this.lastLoadPromise]);
        const Component = await loaderPromise;
        this.questionsMapping[config.name]!.component = Component;
        return { default: Component };
      }),
    };
  }
}

export const engine = new PollEngine();

engine.addQuestionAsync(PollQuestions.PrototypeScreen, PrototypeScreen);

engine.addQuestionAsync(PollQuestions.SinglePoll, SinglePoll);

engine.addQuestionAsync(PollQuestions.MaxDiffPoll, MaxDiffPoll);

engine.addQuestionAsync(PollQuestions.ChoiceConjoint, ChoiceConjoint);

engine.addQuestionAsync(PollQuestions.MultiplePoll, MultiplePoll);

engine.addQuestionAsync(PollQuestions.SingleFromPicturePoll, SingleFromPicturePoll);

engine.addQuestionAsync(PollQuestions.MultipleFromPicturePoll, MultipleFromPicturePoll);

engine.addQuestionAsync(PollQuestions.RankPoll, RankPoll);

engine.addQuestionAsync(PollQuestions.RatingDigitsPoll, RatingDigitsPoll);

engine.addQuestionAsync(PollQuestions.RatingLikePoll, RatingLikePoll);

engine.addQuestionAsync(PollQuestions.RatingSliderPoll, RatingSliderPoll);

engine.addQuestionAsync(PollQuestions.RatingSmiliesPoll, RatingSmilesPoll);

engine.addQuestionAsync(PollQuestions.RatingStarsPoll, RatingStarsPoll);

engine.addQuestionAsync(PollQuestions.TextareaPoll, TextareaPoll);

engine.addQuestionAsync(PollQuestions.SelectPoll, SelectPoll);

engine.addQuestionAsync(PollQuestions.MultipleSelectPoll, MultipleSelectPoll);

engine.addQuestionAsync(PollQuestions.MatrixSinglePoll, MatrixSinglePoll);

engine.addQuestionAsync(PollQuestions.MatrixMultiplePoll, MatrixMultiplePoll);

engine.addQuestionAsync(PollQuestions.MatrixRatingDigitsPoll, MatrixRatingDigitsPoll);

engine.addQuestionAsync(PollQuestions.MatrixRatingStarsPoll, MatrixRatingStarsPoll);

engine.addQuestionAsync(PollQuestions.ClickMapPoll, ClickMapPoll);

engine.addQuestionAsync(PollQuestions.FilePoll, FilePoll);

engine.addQuestionAsync(PollQuestions.MediaFileAny, MediaFileAny);

engine.addQuestionAsync(PollQuestions.MediaFileImage, MediaFileImage);

engine.addQuestionAsync(PollQuestions.MediaFileAudio, MediaFileAudio);

engine.addQuestionAsync(PollQuestions.MediaFileVideo, MediaFileVideo);

engine.addQuestionAsync(PollQuestions.MediaFileText, MediaFileText);

engine.addQuestionAsync(PollQuestions.AuthStep, AuthPoll);

engine.addQuestionAsync(PollQuestions.CardSortingCategoryPoll, CardSortingCategoryPoll);

engine.addWelcome(PollWelcomes.Welcome, { default: Welcome });

engine.addWelcome(PollWelcomes.MobileStoriesWelcome, { default: WelcomeMobileStories });

engine.addFinal(PollFinals.Final, { default: Final });
